import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2 } from "lucide-react";
import { useState, type ComponentProps, type ReactNode } from "react";
import { parsePrice, parseQuantity } from "./lineItemNumbers";

export interface LineItemDraft {
  name: string;
  description: string;
  pricing_type: string;
  unit_price: number;
  quantity: number;
  line_total: number;
  menu_item_id?: string;
  package_id?: string;
}

import { usePricingTypes, getPricingLabel, getUnitLabel } from "@/hooks/usePricingTypes";

interface QuoteLineItemRowProps {
  item: LineItemDraft;
  index: number;
  onChange: (index: number, field: string, value: string | number) => void;
  onRemove: (index: number) => void;
  /** Rendered at the start of the row (the drag handle in the quote form). */
  dragHandle?: ReactNode;
}

interface DraftNumberInputProps extends Omit<ComponentProps<typeof Input>, "value" | "onChange" | "onBlur"> {
  /** What the box shows when nobody is typing in it. */
  idleText: string | number;
  /** The number to commit for the text, or null when the text can't be committed. */
  parse: (text: string) => number | null;
  /** Committed on blur when the box is left empty or invalid. */
  fallback: number;
  onValue: (value: number) => void;
}

/**
 * A number box that can be cleared while typing (#996). The typed text lives in
 * a local draft, so deleting the value doesn't snap it back; only valid numbers
 * reach `onValue`, and blur commits `fallback` for an empty or invalid box.
 * While idle the box shows `idleText`, so outside updates (per-person lines
 * following the guest count) show straight away.
 */
export function DraftNumberInput({ idleText, parse, fallback, onValue, ...props }: DraftNumberInputProps) {
  const [draft, setDraft] = useState<string | null>(null);

  return (
    <Input
      {...props}
      value={draft ?? idleText}
      onChange={(e) => {
        setDraft(e.target.value);
        const n = parse(e.target.value);
        if (n !== null) onValue(n);
      }}
      onBlur={() => {
        if (draft !== null && parse(draft) === null) onValue(fallback);
        setDraft(null);
      }}
    />
  );
}

export function QuoteLineItemRow({ item, index, onChange, onRemove, dragHandle }: QuoteLineItemRowProps) {
  const { data: pricingTypes } = usePricingTypes();
  const isCustom = !item.menu_item_id && !item.package_id;
  const itemLabel = item.name || "line item";

  return (
    <div className="border rounded-lg p-3 bg-card space-y-1.5">
      {/* Below sm the row wraps: handle + name/description on one full-width line,
          price/qty/total/remove underneath. At sm+ both wrappers are display:contents,
          so their children sit in a single flex row as before. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
        <div className="flex items-start gap-3 sm:contents">
          {dragHandle}

          {/* Name & description */}
          <div className="flex-1 min-w-0">
            {/* Name and description are editable on every line, catalog ones
                included. They're a per-quote copy, so edits never touch the menu
                item or package. The description renders under the item on the invoice. */}
            <Input
              value={item.name}
              onChange={(e) => onChange(index, "name", e.target.value)}
              placeholder="Item name"
              aria-label={`Name for ${itemLabel}`}
              className="text-sm font-medium h-8"
            />
            <Textarea
              value={item.description}
              onChange={(e) => onChange(index, "description", e.target.value)}
              placeholder="Description (optional) — shown under the item on the invoice…"
              rows={1}
              aria-label={`Description for ${itemLabel}`}
              className="text-base md:text-xs mt-1 min-h-8 resize-y"
            />
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal">
                {getPricingLabel(pricingTypes, item.pricing_type)}
              </Badge>
              {!isCustom && (
                <span className="text-[10px] text-muted-foreground">Catalog item · edits apply to this quote only</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-start justify-end gap-3 sm:contents">
          {/* Price */}
          <div className="space-y-0.5 text-right shrink-0">
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">$</span>
              <DraftNumberInput
                type="number"
                step="0.01"
                min="0"
                idleText={item.unit_price || ""}
                parse={parsePrice}
                fallback={0}
                onValue={(n) => onChange(index, "unit_price", n)}
                aria-label={`Unit price for ${itemLabel}`}
                className="w-20 text-sm text-right h-8"
              />
            </div>
            <div className="text-[10px] text-muted-foreground">/{getUnitLabel(pricingTypes, item.pricing_type).replace(/s$/, "")}</div>
          </div>

          {/* Quantity */}
          <div className="space-y-0.5 text-center shrink-0">
            <DraftNumberInput
              type="number"
              min="1"
              idleText={item.quantity}
              parse={parseQuantity}
              fallback={1}
              onValue={(n) => onChange(index, "quantity", n)}
              aria-label={`Quantity for ${itemLabel}`}
              className="w-16 text-sm text-center h-8"
            />
            <div className="text-[10px] text-muted-foreground">{getUnitLabel(pricingTypes, item.pricing_type)}</div>
          </div>

          {/* Total */}
          <div className="w-20 text-right shrink-0 pt-1">
            <div className="text-sm font-semibold">${item.line_total.toFixed(2)}</div>
          </div>

          {/* Remove */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => onRemove(index)}
            aria-label={`Remove ${itemLabel}`}
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      </div>
    </div>
  );
}
